import { FC, useEffect, useMemo, useRef } from "react";
import {
  Button,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormDialog,
  FormDialogCancel,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Separator,
  Spinner,
} from "@capacit-com/lara-law-shared-ui";
import { zodResolver } from "@hookform/resolvers/zod";
import { FileIcon, Pencil } from "lucide-react";
import { useForm } from "react-hook-form";
import Workspace from "@/models/workspace/Workspace";
import {
  isCloudLinkedItem,
  Item,
} from "@/pages/workspace/utils/workspaceItemUtils";
import {
  buildDocumentName,
  buildRenameDocumentSchema,
  getSiblingDocumentNames,
  RenameDocumentFormValues,
  splitDocumentName,
} from "@/pages/workspace/utils/documentRename";

export type RenameDocumentDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  workspace: Workspace | null;
  item: Item | null;
  /**
   * Called with the full new name — the edited base name plus the file's unchanged
   * extension. Only invoked when the name actually changed.
   */
  onRename: (name: string) => Promise<void>;
  isPending: boolean;
};

const RenameDocumentDialog: FC<RenameDocumentDialogProps> = ({
  isOpen,
  onClose,
  workspace,
  item,
  onRename,
  isPending,
}) => {
  const { baseName, extension } = useMemo(
    () => splitDocumentName(item?.name ?? ""),
    [item?.name]
  );
  const siblingNames = useMemo(
    () => getSiblingDocumentNames(workspace, item?.id),
    [workspace, item?.id]
  );
  const isCloudLinked = item !== null && isCloudLinkedItem(item);

  // react-hook-form re-reads `resolver` on every render, so the rules always see the
  // extension of the file that is open right now and the sibling list as the
  // workspace looks now — including the first render after a different file is
  // picked from the dropdown.
  const form = useForm<RenameDocumentFormValues>({
    resolver: zodResolver(buildRenameDocumentSchema(extension, siblingNames)),
    defaultValues: { baseName },
  });

  // Re-seed the field whenever a different file is picked from the menu — the dialog
  // stays mounted, so the previous file's name would otherwise stick.
  useEffect(() => {
    if (isOpen) form.reset({ baseName });
  }, [isOpen, item?.id, baseName, form]);

  // `isPending` only covers the workspace write. For a cloud-linked file `onRename`
  // first renames the OneDrive/SharePoint copy — which can take as long as a consent
  // popup — so the dialog is busy for the whole submit, not just the mutation, or Gem
  // could fire a second rename and Annuller could close over one still in flight.
  const busy = isPending || form.formState.isSubmitting;
  const inFlight = useRef(false);

  const handleSubmit = form.handleSubmit(async (values) => {
    if (inFlight.current) return;
    if (!item) {
      onClose();
      return;
    }

    const newName = buildDocumentName(item.name, values.baseName);
    if (newName === item.name) {
      onClose();
      return;
    }

    inFlight.current = true;
    try {
      await onRename(newName);
      onClose();
    } catch {
      // onRename reports the failure; keep the dialog open so the name is not lost.
    } finally {
      inFlight.current = false;
    }
  });

  return (
    <FormDialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open && !busy) onClose();
      }}
      isDirty={form.formState.isDirty}
      size="md"
    >
      <DialogHeader className="space-y-3">
        <div className="flex items-center gap-2">
          <div className="bg-primary/10 p-2 rounded-full">
            <FileIcon className="h-5 w-5 text-primary" />
          </div>
          <DialogTitle className="text-xl">Omdøb fil</DialogTitle>
        </div>
        <DialogDescription className="text-sm text-muted-foreground">
          Giv filen et nyt navn. Indholdet
          {extension ? ` og filtypen (${extension})` : ""} bliver ikke ændret.
          {isCloudLinked && (
            <span
              className="block mt-1"
              data-testid="rename-document-cloud-note"
            >
              Filen omdøbes også i OneDrive/SharePoint.
            </span>
          )}
        </DialogDescription>
      </DialogHeader>

      <Separator className="my-1" />

      <Form {...form}>
        <form onSubmit={handleSubmit} className="space-y-6">
          <FormField
            control={form.control}
            name="baseName"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="flex items-center gap-1.5">
                  <Pencil className="h-3.5 w-3.5 text-primary" />
                  Filnavn
                </FormLabel>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="F.eks. Rammeaftale"
                        className="pl-9"
                        autoFocus
                        data-testid="rename-document-name"
                      />
                    </FormControl>
                    <FileIcon className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                  </div>
                  {extension && (
                    <span
                      className="text-sm text-muted-foreground shrink-0"
                      data-testid="rename-document-extension"
                    >
                      {extension}
                    </span>
                  )}
                </div>
                <FormMessage />
              </FormItem>
            )}
          />

          <DialogFooter className="gap-2 space-x-2 sm:gap-0 mt-6">
            <FormDialogCancel disabled={busy} />
            <Button
              type="submit"
              className="gap-2 min-w-[100px]"
              disabled={busy}
            >
              {busy ? (
                <Spinner className="h-4 w-4 text-primary-foreground" />
              ) : (
                <span className="flex items-center gap-2">
                  <Pencil className="h-4 w-4" />
                  Gem
                </span>
              )}
            </Button>
          </DialogFooter>
        </form>
      </Form>
    </FormDialog>
  );
};

export default RenameDocumentDialog;
